import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Pencil, Trash2, Lock, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InfraChecklist from "@/components/admin/InfraChecklist";
import AffiliatesAdminTab from "@/components/admin/AffiliatesAdminTab";
import GuestsAdminTab from "@/components/admin/GuestsAdminTab";
import DashboardTab from "@/components/admin/DashboardTab";
import InactivityReviewTab from "@/components/admin/InactivityReviewTab";
import RetentionAuditLog from "@/components/admin/RetentionAuditLog";
import PageContentEditor from "@/components/admin/PageContentEditor";

interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  body: string;
  image_url: string | null;
  category: string;
  tags: string[];
  author: string;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

const emptyArticle = {
  title: "",
  excerpt: "",
  body: "",
  image_url: "",
  category: "General",
  tags: [] as string[],
  author: "The Emerging Market",
  published: false,
};

const Admin = () => {
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [editing, setEditing] = useState<Partial<Article> | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);

  const callApi = async (action: string, article?: any) => {
    const { data, error } = await supabase.functions.invoke("manage-articles", {
      body: { action, article },
      headers: { "x-admin-password": password },
    });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const handleLogin = async () => {
    try {
      const data = await callApi("list");
      setArticles(data.articles);
      setAuthenticated(true);
      toast.success("Welcome to the content manager");
    } catch {
      toast.error("Invalid password");
    }
  };

  const loadArticles = async () => {
    try {
      const data = await callApi("list");
      setArticles(data.articles);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleSave = async () => {
    if (!editing?.title || !editing?.body) {
      toast.error("Title and body are required");
      return;
    }
    setLoading(true);
    try {
      const action = editing.id ? "update" : "create";
      await callApi(action, editing);
      toast.success(editing.id ? "Article updated" : "Article created");
      setEditing(null);
      await loadArticles();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this article?")) return;
    try {
      await callApi("delete", { id });
      toast.success("Article deleted");
      await loadArticles();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && editing) {
      setEditing({ ...editing, tags: [...(editing.tags || []), tagInput.trim()] });
      setTagInput("");
    }
  };

  const removeTag = (index: number) => {
    if (editing) {
      const newTags = [...(editing.tags || [])];
      newTags.splice(index, 1);
      setEditing({ ...editing, tags: newTags });
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="bg-card border border-border rounded-xl p-8 w-full max-w-sm">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="h-5 w-5 text-primary" />
            <h1 className="font-display text-xl font-bold">Content Manager</h1>
          </div>
          <Input
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            className="mb-4 bg-background border-border"
          />
          <Button onClick={handleLogin} className="w-full bg-primary text-primary-foreground">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-6 py-8 max-w-3xl">
          <Button variant="ghost" onClick={() => setEditing(null)} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Articles
          </Button>

          <h1 className="font-display text-2xl font-bold mb-6">
            {editing.id ? "Edit Article" : "New Article"}
          </h1>

          <div className="space-y-5">
            <div>
              <Label className="text-sm font-medium">Title</Label>
              <Input
                value={editing.title || ""}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                className="mt-1 bg-background border-border"
                placeholder="Article title"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Author</Label>
              <Input
                value={editing.author || ""}
                onChange={(e) => setEditing({ ...editing, author: e.target.value })}
                className="mt-1 bg-background border-border"
                placeholder="Author name"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Category</Label>
              <Input
                value={editing.category || ""}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className="mt-1 bg-background border-border"
                placeholder="e.g. Policy Analysis, Impact Story"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                  className="bg-background border-border"
                  placeholder="Add a tag and press Enter"
                />
                <Button type="button" variant="outline" onClick={addTag} size="sm">Add</Button>
              </div>
              {(editing.tags?.length ?? 0) > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {editing.tags?.map((tag, i) => (
                    <span key={i} className="text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full flex items-center gap-1">
                      {tag}
                      <button onClick={() => removeTag(i)} className="hover:text-destructive">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium">Image URL</Label>
              <Input
                value={editing.image_url || ""}
                onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                className="mt-1 bg-background border-border"
                placeholder="https://..."
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Excerpt</Label>
              <Textarea
                value={editing.excerpt || ""}
                onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })}
                className="mt-1 bg-background border-border"
                rows={2}
                placeholder="Brief summary for article cards"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Body</Label>
              <Textarea
                value={editing.body || ""}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                className="mt-1 bg-background border-border font-mono text-sm"
                rows={16}
                placeholder="Article content (supports markdown)"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Switch
                checked={editing.published || false}
                onCheckedChange={(checked) => setEditing({ ...editing, published: checked })}
              />
              <Label className="text-sm">
                {editing.published ? (
                  <span className="flex items-center gap-1.5 text-primary"><Eye className="h-4 w-4" /> Published</span>
                ) : (
                  <span className="flex items-center gap-1.5 text-muted-foreground"><EyeOff className="h-4 w-4" /> Draft</span>
                )}
              </Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleSave} disabled={loading} className="bg-primary text-primary-foreground">
                {loading ? "Saving..." : "Save Article"}
              </Button>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8 max-w-6xl">
        <div className="mb-6">
          <Link to="/insights" className="text-sm text-primary hover:underline mb-1 block">← Back to Insights</Link>
          <h1 className="font-display text-2xl font-bold">Admin</h1>
        </div>

        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="content">Content Manager</TabsTrigger>
            <TabsTrigger value="copy">Page Copy</TabsTrigger>
            <TabsTrigger value="affiliates">Affiliates</TabsTrigger>
            <TabsTrigger value="guests">Guests</TabsTrigger>
            <TabsTrigger value="retention">Retention</TabsTrigger>
            <TabsTrigger value="infra">Infrastructure</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardTab password={password} />
          </TabsContent>

          <TabsContent value="content">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-semibold">Articles</h2>
              <Button onClick={() => setEditing({ ...emptyArticle })} className="bg-primary text-primary-foreground">
                <Plus className="h-4 w-4 mr-2" /> New Article
              </Button>
            </div>

            {articles.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <p className="text-lg mb-2">No articles yet</p>
            <p className="text-sm">Create your first article to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {articles.map((article) => (
              <div
                key={article.id}
                className="bg-card border border-border rounded-xl p-5 flex items-center gap-4 hover:border-primary/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      article.published
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}>
                      {article.published ? "Published" : "Draft"}
                    </span>
                    <span className="text-xs text-muted-foreground">{article.category}</span>
                  </div>
                  <h3 className="font-display font-semibold truncate">{article.title}</h3>
                  {article.excerpt && (
                    <p className="text-sm text-muted-foreground truncate mt-0.5">{article.excerpt}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing(article);
                      setTagInput("");
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(article.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
            )}
          </TabsContent>

          <TabsContent value="infra">
            <InfraChecklist />
          </TabsContent>

          <TabsContent value="copy">
            <PageContentEditor password={password} />
          </TabsContent>

          <TabsContent value="affiliates">
            <AffiliatesAdminTab password={password} />
          </TabsContent>

          <TabsContent value="guests">
            <GuestsAdminTab password={password} />
          </TabsContent>

          <TabsContent value="retention">
            <InactivityReviewTab />
            <RetentionAuditLog />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
